import { z } from 'zod';

import { zodTimeStamp } from '@/utils/zodUtils';

import { TierOveragePolicy } from './tierCapacityPolicy';

export const OVERAGE_USAGE_MAX_DAYS = 93;

export enum OverageBillingStatus {
  NONE = 'none',
  PENDING = 'pending',
  INVOICED = 'invoiced',
  CANCELED = 'canceled',
  ESTIMATED = 'estimated',
}

// A local calendar day at the agreement's location, not an instant.
const zodServiceDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

const OverageRevenueSplitSchema = z.object({
  operator_amount_cents: z.number().int(),
  platform_fee_cents: z.number().int(),
});

export const OverageDayBreakdownSchema = OverageRevenueSplitSchema.extend({
  rate_cents: z.number().int(),
  convenience_fee_cents: z.number().int(),
  convenience_fee_covered: z.boolean(),
});

export const OverageUsageDaySchema = z.object({
  service_date: zodServiceDate,
  estimated: z.boolean(),
  as_of: zodTimeStamp.optional(),
  measurement_version: z.number().int(),
  entry_vehicles: z.number().int().min(0),
  exit_vehicles: z.number().int().min(0),
  counted_vehicles: z.number().int().min(0),
  baseline_seats: z.number().int().min(0),
  overage_vehicles: z.number().int().min(0),
  unit_amount_cents: z.number().int().nullable(),
  amount_cents: z.number().int().nullable(),
  billing_status: z.nativeEnum(OverageBillingStatus),
  invoice_id: z.string().nullable(),
  // Operators only, on days that billed overage.
  breakdown: OverageDayBreakdownSchema.optional(),
});

export const OverageUsageSummarySchema = z.object({
  peak_date: zodServiceDate.nullable(),
  peak_vehicles: z.number().int().min(0),
  overage_days: z.number().int().min(0),
  vehicle_days: z.number().int().min(0),
  amount_cents: z.number().int(),
  pending_amount_cents: z.number().int(),
  // Operators only.
  operator_amount_cents: z.number().int().optional(),
  platform_fee_cents: z.number().int().optional(),
});

export const GroupOverageUsageSchema = z.object({
  from: zodServiceDate,
  to: zodServiceDate,
  baseline_seats: z.number().int().min(0),
  overage_cap: z.number().int().nullable(),
  overage_billing_since: zodServiceDate.nullable(),
  latest_closed_date: zodServiceDate.nullable(),
  summary: OverageUsageSummarySchema,
  days: z.array(OverageUsageDaySchema),
});

export const OverageCountedVehicleSchema = z.object({
  service_date: zodServiceDate,
  plate: z.string(),
  tier_id: z.string().uuid().nullable(),
  member: z.object({
    name: z.string().nullable(),
    email: z.string().nullable(),
    removed: z.boolean(),
  }),
});

export const GroupOverageCountedVehiclesSchema = z.object({
  plates: z.array(OverageCountedVehicleSchema),
});

// An omitted key leaves the stored value untouched; a null cap clears it.
export interface UpdateGroupOverageSettingsRequest {
  policy?: TierOveragePolicy;
  cap?: number | null;
}

export type OverageDayBreakdownType = z.infer<typeof OverageDayBreakdownSchema>;
export type OverageUsageDayType = z.infer<typeof OverageUsageDaySchema>;
export type OverageUsageSummaryType = z.infer<typeof OverageUsageSummarySchema>;
export type GroupOverageUsageType = z.infer<typeof GroupOverageUsageSchema>;
export type OverageCountedVehicleType = z.infer<
  typeof OverageCountedVehicleSchema
>;
export type GroupOverageCountedVehiclesType = z.infer<
  typeof GroupOverageCountedVehiclesSchema
>;
